/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Converted from TypeScript
 */

import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloudIcon } from './icons';
import { Compare } from './Compare';
import { generateModelImage } from '../../services/geminiService';
import Spinner from './Spinner';
import { getFriendlyErrorMessage } from '../../lib/utils';
import {
  saveUploadedPhotoToSession,
  getUploadedPhotoFromSession,
  saveModelImageToSession,
  getModelImageFromSession,
  clearUploadedPhotoSession,
  clearModelImageSession,
} from '../../utils/sessionStorage';

export default function StartScreen({ onModelFinalized }) {
  const [userImageUrl, setUserImageUrl] = useState(null);
  const [generatedModelUrl, setGeneratedModelUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Restore from session on mount
  useEffect(() => {
    const savedPhoto = getUploadedPhotoFromSession();
    const savedModel = getModelImageFromSession();

    if (savedPhoto) {
      setUserImageUrl(savedPhoto);
    }

    if (savedModel) {
      setGeneratedModelUrl(savedModel);
    }
  }, []);

  const handleFileSelect = useCallback(async (file) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result;
      setUserImageUrl(dataUrl);
      saveUploadedPhotoToSession(dataUrl);
      setIsGenerating(true);
      setGeneratedModelUrl(null);
      setError(null);
      try {
        const result = await generateModelImage(file);
        setGeneratedModelUrl(result);
        saveModelImageToSession(result);
      } catch (err) {
        setError(getFriendlyErrorMessage(err, 'Failed to generate model'));
        setUserImageUrl(null);
        clearUploadedPhotoSession();
      } finally {
        setIsGenerating(false);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const reset = () => {
    setUserImageUrl(null);
    setGeneratedModelUrl(null);
    setIsGenerating(false);
    setError(null);
    clearUploadedPhotoSession();
    clearModelImageSession();
  };

  const screenVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  };

  return (
    <AnimatePresence mode="wait">
      {!userImageUrl ? (
        <motion.div
          key="uploader"
          className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12"
          variants={screenVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <div className="lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left">
            <div className="max-w-lg">
              <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 leading-tight">
                Create Your Model
              </h1>
              <p className="mt-4 text-lg text-gray-600">
                Ever wondered how an outfit would look on you? Stop guessing.
                Upload your photo and see instantly. Our AI creates your personal model, ready to try on any garment.
              </p>
              <hr className="my-8 border-gray-200" />
              <div className="flex flex-col items-center lg:items-start w-full gap-3">
                <label
                  htmlFor="image-upload-start"
                  className="w-full relative flex items-center justify-center px-8 py-3 text-base font-semibold text-white
  bg-gradient-to-r from-red-600 to-red-500
  hover:from-red-700 hover:to-red-600
  rounded-lg cursor-pointer shadow transition-all duration-300 hover:scale-105"
                >

                  <UploadCloudIcon className="w-5 h-5 mr-3" />
                  Upload Photo
                </label>
                <input id="image-upload-start" type="file" className="hidden" accept="image/png, image/jpeg, image/webp, image/avif, image/heic, image/heif" onChange={handleFileChange} />
                <p className="text-gray-500 text-sm">Choose a clear full-body photo. Face-only works too, but full-body gives best results.</p>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              </div>
            </div>
          </div>
          <div className="w-full lg:w-1/2 flex flex-col items-center justify-center">
            <Compare
              firstImage="https://storage.googleapis.com/gemini-95-icons/asr-tryon.jpg"
              secondImage="https://storage.googleapis.com/gemini-95-icons/asr-tryon-model.png"
              slideMode="drag"
              className="w-full max-w-sm aspect-[2/3] rounded-2xl bg-gray-200"
            />
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="compare"
          className="w-full max-w-6xl mx-auto h-full flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12"
          variants={screenVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <div className="md:w-1/2 flex-shrink-0 flex flex-col items-center md:items-start">
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 leading-tight">
                Your New Model
              </h1>
              <p className="mt-2 text-md text-gray-600">
                Drag the slider to see the transformation.
              </p>
            </div>

            {isGenerating && (
              <div className="flex items-center gap-3 text-lg text-gray-700 font-serif mt-6">
                <Spinner />
                <span>Creating your model...</span>
              </div>
            )}

            {error &&
              <div className="text-center md:text-left text-red-600 max-w-md mt-6">
                <p className="font-semibold">Model Generation Failed</p>
                <p className="text-sm mb-4">{error}</p>
                <button onClick={reset} className="text-sm font-semibold text-gray-700 hover:underline">Try Again</button>
              </div>
            }

            <AnimatePresence>
              {generatedModelUrl && !isGenerating && !error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col sm:flex-row items-center gap-4 mt-8"
                >
                  <button
                    onClick={reset}
                    className="w-full sm:w-auto px-6 py-3 text-base font-semibold text-gray-700 bg-gray-200 rounded-lg cursor-pointer hover:bg-gray-300 transition-colors"
                  >
                    Use Different Photo
                  </button>
                  <button
                    onClick={() => onModelFinalized(generatedModelUrl)}
                    className="w-full sm:w-auto relative inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-blue-600 rounded-lg cursor-pointer group hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    Continue to Try-On &rarr;
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="md:w-1/2 w-full flex items-center justify-center">
            <div
              className={`relative rounded-[1.25rem] transition-all duration-700 ease-in-out ${isGenerating ? 'border border-gray-300 animate-pulse' : 'border border-transparent'}`}
            >
              <Compare
                firstImage={userImageUrl}
                secondImage={generatedModelUrl ?? userImageUrl}
                slideMode="drag"
                className="w-[280px] h-[420px] sm:w-[320px] sm:h-[480px] lg:w-[400px] lg:h-[600px] rounded-2xl bg-gray-200"
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
